// ─── NewsletterWidget.tsx — Bloc d'abonnement à la newsletter ────────────────
import { useState, FormEvent } from 'react';
import { Mail, ArrowRight, CheckCircle, Loader, Newspaper, ShieldCheck, CalendarDays, X } from 'lucide-react';
import { apiClient } from '../lib/api';
import { useLanguage } from '../i18n/LanguageContext';

type Status = 'idle' | 'loading' | 'success' | 'error';

interface Props {
  /** Pré-remplir avec l'email de l'utilisateur connecté */
  prefillEmail?: string;
  /** Variante compacte (inline) ou étendue (avec description) */
  variant?: 'compact' | 'full';
}

export default function NewsletterWidget({ prefillEmail = '', variant = 'full' }: Props) {
  const { lang } = useLanguage();
  const [email, setEmail]         = useState(prefillEmail);
  const [website, setWebsite]     = useState('');   // honeypot anti-bot — doit rester vide
  const [status, setStatus]       = useState<Status>('idle');
  const [error, setError]         = useState('');
  const [showUnsub, setShowUnsub] = useState(false);
  const [unsubStatus, setUnsubStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');

  async function handleUnsubscribe() {
    if (!email.trim()) return;
    setUnsubStatus('loading');
    try {
      await apiClient.post('/newsletter/unsubscribe', { email: email.trim().toLowerCase() });
      setUnsubStatus('done');
    } catch {
      setUnsubStatus('error');
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    setError('');
    try {
      await apiClient.post('/newsletter/subscribe', { email: email.trim().toLowerCase(), language: lang, website });
      setStatus('success');
    } catch {
      setError(
        lang === 'fr'
          ? 'Une erreur est survenue. Veuillez réessayer.'
          : 'Something went wrong. Please try again.'
      );
      setStatus('error');
    }
  }

  // ── État succès ──────────────────────────────────────────────────────────
  if (status === 'success') {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 flex items-start gap-3">
        <CheckCircle size={18} className="text-emerald-500 shrink-0 mt-0.5" />
        <div>
          <p className="text-[var(--color-ink)] text-sm font-semibold">
            {lang === 'fr' ? 'Vérifiez votre boîte mail !' : 'Check your inbox!'}
          </p>
          <p className="text-[var(--color-muted)] text-xs mt-0.5">
            {lang === 'fr'
              ? 'Un email de confirmation vous a été envoyé. Cliquez sur le lien pour finaliser votre inscription.'
              : 'A confirmation email has been sent. Click the link to complete your subscription.'}
          </p>
        </div>
      </div>
    );
  }

  // ── Variante compacte ────────────────────────────────────────────────────
  if (variant === 'compact') {
    return (
      <form onSubmit={handleSubmit} className="flex gap-2 flex-wrap">
        {/* Honeypot anti-bot — invisible pour les humains, rempli par les bots */}
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          value={website}
          onChange={e => setWebsite(e.target.value)}
          style={{ position: 'absolute', left: '-9999px', top: 'auto', width: 1, height: 1, opacity: 0 }}
        />
        <input
          type="email"
          required
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder={lang === 'fr' ? 'votre@email.com' : 'your@email.com'}
          className="flex-1 min-w-0 bg-[var(--color-inset)] border border-[var(--color-border)] rounded-xl px-3 py-2 text-sm text-[var(--color-ink)] placeholder-[var(--color-faint)] focus:outline-none focus:border-[var(--color-accent)] transition"
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          className="sku-btn-primary px-4 py-2 text-sm shrink-0"
        >
          {status === 'loading'
            ? <Loader size={14} className="animate-spin" />
            : <ArrowRight size={14} />}
          {lang === 'fr' ? "S'abonner" : 'Subscribe'}
        </button>
        {status === 'error' && (
          <p className="w-full text-xs text-red-600 mt-0.5">{error}</p>
        )}
      </form>
    );
  }

  // ── Variante complète ────────────────────────────────────────────────────
  return (
    <div className="sku-card rounded-2xl p-6">
      {/* En-tête */}
      <div className="flex items-center gap-2.5 mb-3">
        <div className="p-1.5 rounded-lg bg-[var(--color-accent-soft)] border border-[rgba(13,148,136,0.2)]">
          <Mail size={15} className="text-[var(--color-accent-strong)]" />
        </div>
        <h3 className="text-[var(--color-ink)] font-bold text-sm">
          {lang === 'fr' ? 'Newsletter sécurité' : 'Security newsletter'}
        </h3>
      </div>

      {/* Description */}
      <p className="text-[var(--color-muted)] text-sm leading-relaxed mb-4">
        {lang === 'fr'
          ? 'Recevez nos conseils en cybersécurité et les nouveaux articles de blog directement dans votre boîte mail. Pas de spam — désabonnement en un clic.'
          : 'Get our cybersecurity tips and new blog articles straight to your inbox. No spam — unsubscribe in one click.'}
      </p>

      {/* Badges */}
      <div className="flex flex-wrap gap-3 mb-4">
        {([
          {
            Icon: Newspaper,
            bg: 'bg-[var(--color-accent-soft)]', border: 'border-[rgba(13,148,136,0.2)]', color: 'text-[var(--color-accent-strong)]',
            fr: 'Nouveaux articles', en: 'New articles',
          },
          {
            Icon: ShieldCheck,
            bg: 'bg-violet-50', border: 'border-violet-200', color: 'text-violet-500',
            fr: 'Conseils sécurité', en: 'Security tips',
          },
          {
            Icon: CalendarDays,
            bg: 'bg-emerald-50', border: 'border-emerald-200', color: 'text-emerald-500',
            fr: 'Mensuel', en: 'Monthly',
          },
        ] as const).map((item) => (
          <div key={item.fr} className="flex items-center gap-2 text-xs text-[var(--color-body)]">
            <div className={`w-6 h-6 rounded-lg border flex items-center justify-center shrink-0 ${item.bg} ${item.border}`}>
              <item.Icon size={11} className={item.color} />
            </div>
            <span>{lang === 'fr' ? item.fr : item.en}</span>
          </div>
        ))}
      </div>

      {/* Formulaire */}
      <form onSubmit={handleSubmit} className="flex gap-2">
        {/* Honeypot anti-bot — invisible pour les humains, rempli par les bots */}
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          value={website}
          onChange={e => setWebsite(e.target.value)}
          style={{ position: 'absolute', left: '-9999px', top: 'auto', width: 1, height: 1, opacity: 0 }}
        />
        <input
          type="email"
          required
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder={lang === 'fr' ? 'votre@email.com' : 'your@email.com'}
          className="flex-1 min-w-0 bg-[var(--color-inset)] border border-[var(--color-border)] rounded-xl px-3 py-2.5 text-sm text-[var(--color-ink)] placeholder-[var(--color-faint)] focus:outline-none focus:border-[var(--color-accent)] transition"
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          className="sku-btn-primary px-4 py-2.5 text-sm shrink-0"
        >
          {status === 'loading'
            ? <Loader size={14} className="animate-spin" />
            : <ArrowRight size={14} />}
          {lang === 'fr' ? "S'abonner" : 'Subscribe'}
        </button>
      </form>

      {status === 'error' && (
        <p className="text-xs text-red-600 mt-2">{error}</p>
      )}

      <div className="flex items-center justify-between mt-3">
        <p className="text-[var(--color-faint)] text-xs">
          {lang === 'fr'
            ? 'Double opt-in — confirmation par email requise.'
            : 'Double opt-in — email confirmation required.'}
        </p>
        {!showUnsub && unsubStatus === 'idle' && (
          <button
            type="button"
            onClick={() => setShowUnsub(true)}
            className="text-[var(--color-faint)] hover:text-[var(--color-muted)] text-xs transition flex items-center gap-1"
          >
            <X size={10} />
            {lang === 'fr' ? 'Se désabonner' : 'Unsubscribe'}
          </button>
        )}
      </div>

      {/* Confirmation désabonnement */}
      {showUnsub && unsubStatus === 'idle' && (
        <div className="mt-3 flex items-center gap-2 p-2.5 rounded-xl bg-[var(--color-inset)] border border-[var(--color-border)]">
          <p className="text-[var(--color-muted)] text-xs flex-1">
            {lang === 'fr'
              ? `Se désabonner avec ${email || 'cet email'} ?`
              : `Unsubscribe ${email || 'this email'} ?`}
          </p>
          <button
            type="button"
            onClick={handleUnsubscribe}
            className="text-xs text-red-600 hover:text-red-700 font-medium transition shrink-0"
          >
            {lang === 'fr' ? 'Confirmer' : 'Confirm'}
          </button>
          <button
            type="button"
            onClick={() => setShowUnsub(false)}
            className="text-xs text-[var(--color-faint)] hover:text-[var(--color-ink)] transition shrink-0"
          >
            {lang === 'fr' ? 'Annuler' : 'Cancel'}
          </button>
        </div>
      )}
      {unsubStatus === 'loading' && (
        <p className="text-xs text-[var(--color-faint)] mt-2 flex items-center gap-1.5">
          <Loader size={11} className="animate-spin" />
          {lang === 'fr' ? 'Désabonnement…' : 'Unsubscribing…'}
        </p>
      )}
      {unsubStatus === 'done' && (
        <p className="text-xs text-[var(--color-muted)] mt-2">
          {lang === 'fr' ? '✓ Vous avez été désabonné.' : '✓ You have been unsubscribed.'}
        </p>
      )}
      {unsubStatus === 'error' && (
        <p className="text-xs text-red-600 mt-2">
          {lang === 'fr' ? 'Erreur lors du désabonnement.' : 'Error while unsubscribing.'}
        </p>
      )}
    </div>
  );
}
